import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import OfferCard from "@/components/base/OfferCard";
import { useAdminDeals, useAdminProducts } from "@/hooks/useAdminStore";
import { useArticleCatalog } from "@/hooks/useArticleCatalog";
import { buildCanonicalUrl, usePageSeo } from "@/hooks/usePageSeo";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { formatCanonicalDateForDisplay } from "@/utils/date";
import { getMarketplaceFulfillmentIcon, getMarketplaceFulfillmentLabel, isAmazonMarketplace } from "@/utils/marketplace";

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

type ProductPageData = {
  id: number;
  name: string;
  slug: string;
  category: string;
  categorySlug: string;
  subcategory: string;
  subcategorySlug: string;
  brand: string;
  model: string;
  image: string;
  images: string[];
  overviewText: string;
  price: number;
  originalPrice: number;
  discount: number;
  rating: number;
  reviews: number;
  marketplace: string;
  affiliateLink: string;
  freeShipping: boolean;
  fulfillment: boolean;
  officialStore: boolean;
  officialStoreName: string;
  badge: string | null;
  pros: string[];
  cons: string[];
  specs: { label: string; value: string }[];
  faqs: { question: string; answer: string }[];
};

function buildDefaultOverviewText(source: {
  name: string;
}) {
  return `${source.name} está cadastrado no catálogo ativo do site. A página usa automaticamente os dados publicados no painel administrativo, mantendo o mesmo layout visual para imagens, preço e conversão.

## Antes de comprar
Confira sempre o preço atualizado, prazo de entrega, reputação do vendedor e as condições da oferta antes de finalizar a compra.`;
}

function normalizeProduct(source: {
  id: number;
  name: string;
  slug: string;
  image: string;
  galleryImages?: string[];
  overviewText?: string;
  price: number;
  originalPrice: number;
  discount: number;
  marketplace: string;
  affiliateLink: string;
  category: string;
  categorySlug: string;
  subcategory?: string;
  subcategorySlug?: string;
  rating?: number;
  reviews?: number;
  badge?: string | null;
  freeShipping?: boolean;
  fulfillment?: boolean;
  officialStore?: boolean;
  officialStoreName?: string;
}): ProductPageData {
  const images = [source.image, ...(source.galleryImages ?? [])].filter(
    (image, index, list) => image && list.indexOf(image) === index
  );
  const fulfillmentLabel = getMarketplaceFulfillmentLabel(source.marketplace);
  const resolvedSubcategory = source.subcategory ?? source.category;
  const resolvedSubcategorySlug = source.subcategorySlug ?? source.categorySlug;
  const officialStoreName = source.officialStoreName?.trim() ?? "";
  const hasOfficialStore = Boolean(source.officialStore || officialStoreName);

  return {
    id: source.id,
    name: source.name,
    slug: source.slug,
    category: source.category,
    categorySlug: source.categorySlug,
    subcategory: resolvedSubcategory,
    subcategorySlug: resolvedSubcategorySlug,
    brand: source.name.split(" ")[0] || "Produto",
    model: source.name,
    image: source.image,
    images,
    overviewText: source.overviewText?.trim() || buildDefaultOverviewText(source),
    price: source.price,
    originalPrice: source.originalPrice,
    discount: source.discount,
    rating: source.rating ?? 4.5,
    reviews: source.reviews ?? 0,
    marketplace: source.marketplace,
    affiliateLink: source.affiliateLink,
    freeShipping: source.freeShipping ?? false,
    fulfillment: source.fulfillment ?? false,
    officialStore: hasOfficialStore,
    officialStoreName,
    badge: source.badge ?? null,
    pros: [
      `Produto cadastrado em ${source.category}${resolvedSubcategory !== source.category ? ` / ${resolvedSubcategory}` : ""}.`,
      `Preço e disponibilidade atualizados a partir do painel administrativo.`,
      source.fulfillment ? `Conta com logística ${fulfillmentLabel}, com estoque ou entrega priorizada pelo marketplace.` : `Venda direcionada para ${source.marketplace}.`,
      hasOfficialStore ? `Oferta identificada como vendida por ${officialStoreName || "loja oficial"}.` : "Verifique no marketplace se a oferta é vendida pela loja oficial.",
      source.freeShipping ? "Conta com frete grátis." : "Prazo e frete podem variar conforme a loja.",
    ],
    cons: [
      "As especificações detalhadas podem variar de acordo com a oferta publicada.",
      "Preço e estoque podem mudar ao longo do dia.",
    ],
    specs: [
      { label: "Categoria", value: source.category },
      { label: "Subcategoria", value: resolvedSubcategory },
      { label: "Marketplace", value: source.marketplace },
      { label: "Loja oficial", value: officialStoreName || "Não informada" },
      { label: "Logística", value: source.fulfillment ? fulfillmentLabel : "Padrão" },
      { label: "Preço atual", value: `R$ ${source.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` },
      { label: "Desconto", value: `${source.discount}%` },
    ],
    faqs: [
      {
        question: "O preço deste produto pode mudar?",
        answer: "Sim. Os preços são atualizados pelas lojas e podem variar ao longo do dia conforme estoque, campanha e forma de pagamento.",
      },
      {
        question: "Onde comprar com mais segurança?",
        answer: `A oferta atual direciona para ${source.marketplace}. Antes de finalizar, confira o vendedor, o prazo de entrega e a política de devolução da loja.`,
      },
    ],
  };
}

export default function ProductDetailPage() {
  const { products } = useAdminProducts();
  const { deals } = useAdminDeals();
  const { articles } = useArticleCatalog();
  const { settings } = useSiteSettings();
  const { slug } = useParams();
  const [activeImage, setActiveImage] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const catalogProducts = useMemo(
    () => Array.from(
      new Map(
        [
          ...products.map((product) => ({
            id: product.id,
            name: product.name,
            slug: product.slug,
            image: product.image,
            galleryImages: product.galleryImages,
            overviewText: product.overviewText,
            price: product.price,
            originalPrice: product.originalPrice,
            discount: product.discount,
            marketplace: product.marketplace,
            affiliateLink: product.affiliateLink,
            category: product.category,
            categorySlug: product.categorySlug,
            subcategory: product.subcategory,
            subcategorySlug: product.subcategorySlug,
            rating: product.rating,
            reviews: product.reviews,
            badge: product.badge,
            freeShipping: product.freeShipping,
            fulfillment: product.fulfillment,
            officialStore: product.officialStore,
            officialStoreName: product.officialStoreName,
          })),
          ...deals.map((deal) => ({
            id: deal.id + 100000,
            name: deal.name,
            slug: deal.slug,
            image: deal.image,
            overviewText: "",
            price: deal.price,
            originalPrice: deal.originalPrice,
            discount: deal.discount,
            marketplace: deal.marketplace,
            affiliateLink: deal.affiliateLink,
            category: deal.category,
            categorySlug: deal.categorySlug,
            subcategory: deal.category,
            subcategorySlug: deal.categorySlug,
            rating: deal.rating,
            reviews: deal.reviews,
            badge: deal.badge,
            freeShipping: deal.freeShipping,
            fulfillment: false,
            officialStore: false,
            officialStoreName: "",
          })),
        ].map((product) => [product.slug, product])
      ).values()
    ),
    [deals, products]
  );

  const product = useMemo(() => {
    const source = catalogProducts.find((item) => item.slug === slug);
    return source ? normalizeProduct(source) : null;
  }, [catalogProducts, slug]);

  usePageSeo({
    title: product ? `${product.name} | ${settings.siteName}` : `Produto não encontrado | ${settings.siteName}`,
    description: product
      ? `${product.name} com preço atualizado, avaliações, desconto e link de afiliado para comprar em ${product.marketplace}.`
      : `O produto solicitado não foi encontrado no catálogo atual do ${settings.siteName}.`,
    canonicalPath: product ? `/produto/${product.slug}` : "/categorias",
    keywords: settings.seoKeywords,
    image: product?.image || settings.logoUrl,
    siteName: settings.siteName,
    ogType: "product",
  });

  const related = useMemo(() => {
    if (!product) return [];

    return catalogProducts.filter(
      (relatedProduct) =>
        relatedProduct.slug !== product.slug &&
        ((relatedProduct.subcategorySlug ?? relatedProduct.categorySlug) === product.subcategorySlug ||
          (relatedProduct.subcategory ?? relatedProduct.category) === product.subcategory)
    );
  }, [catalogProducts, product]);

  const relatedArticles = useMemo(() => {
    if (!product) return [];

    return articles
      .filter(
        (article) =>
          article.categorySlug === product.subcategorySlug ||
          article.categorySlug === product.categorySlug
      )
      .slice(0, 3);
  }, [articles, product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-20 min-h-[70vh] flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
              <i className="ri-shopping-bag-line text-2xl text-gray-300"></i>
            </div>
            <h1 className="text-2xl font-black text-gray-900 mb-2">Produto não encontrado</h1>
            <p className="text-gray-500 text-sm mb-6">
              Não encontramos esse produto no catálogo atual. Você pode continuar navegando pelas categorias ou pelas ofertas do dia.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/categorias"
                className="px-5 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold transition-colors"
              >
                Ver categorias
              </Link>
              <Link
                to="/ofertas"
                className="px-5 py-3 rounded-xl border border-gray-200 text-gray-700 hover:border-orange-300 hover:text-orange-500 font-bold transition-colors"
              >
                Ver ofertas
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const fulfillmentLabel = getMarketplaceFulfillmentLabel(product.marketplace);
  const fulfillmentIcon = getMarketplaceFulfillmentIcon(product.marketplace);
  const fulfillmentColors = isAmazonMarketplace(product.marketplace)
    ? "bg-amber-50 text-amber-700"
    : "bg-sky-50 text-sky-700";
  const productCategoryPath = product.categorySlug || slugify(product.category);
  const productPath = `/produto/${product.slug}`;
  const productUrl = buildCanonicalUrl(productPath);
  const productDescription = `${product.name} com preço atualizado, avaliações, desconto e link de afiliado para comprar em ${product.marketplace}.`;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: product.images,
    description: productDescription,
    sku: String(product.id),
    mpn: product.model,
    brand: {
      "@type": "Brand",
      name: product.brand,
    },
    category: `${product.category}${product.subcategory !== product.category ? ` / ${product.subcategory}` : ""}`,
    url: productUrl,
    ...(product.reviews > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: product.rating.toFixed(1),
            reviewCount: product.reviews,
          },
        }
      : {}),
    offers: {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: "BRL",
      price: product.price.toFixed(2),
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: {
        "@type": "Organization",
        name: product.officialStoreName || product.marketplace,
      },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: buildCanonicalUrl("/") },
      { "@type": "ListItem", position: 2, name: "Categorias", item: buildCanonicalUrl("/categorias") },
      { "@type": "ListItem", position: 3, name: product.category, item: buildCanonicalUrl(`/categoria/${productCategoryPath}`) },
      ...(product.subcategory !== product.category
        ? [{ "@type": "ListItem", position: 4, name: product.subcategory, item: buildCanonicalUrl(`/categoria/${productCategoryPath}/${product.subcategorySlug}`) }]
        : []),
      {
        "@type": "ListItem",
        position: product.subcategory !== product.category ? 5 : 4,
        name: product.name,
        item: productUrl,
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: product.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />
      <div className="pt-20">
        <div className="bg-gray-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
              <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/categorias" className="hover:text-orange-500 transition-colors">Categorias</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/categoria/${productCategoryPath}`} className="hover:text-orange-500 transition-colors">{product.category}</Link>
              {product.subcategory !== product.category && (
                <>
                  <i className="ri-arrow-right-s-line"></i>
                  <Link to={`/categoria/${productCategoryPath}/${product.subcategorySlug}`} className="hover:text-orange-500 transition-colors">{product.subcategory}</Link>
                </>
              )}
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-gray-600 line-clamp-1">{product.name}</span>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div className="space-y-6">
              <div className="flex flex-col-reverse sm:flex-row gap-4 items-start">
                <div className="flex sm:flex-col gap-3 w-full sm:w-auto">
                  {product.images.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      onClick={() => setActiveImage(index)}
                      className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 bg-gradient-to-b from-gray-50 to-white p-2 ${activeImage === index ? "border-orange-500" : "border-gray-100 hover:border-gray-300"}`}
                    >
                      <img src={image} alt="" className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
                <div className="flex-1 w-full h-96 bg-gradient-to-b from-gray-50 to-white rounded-2xl overflow-hidden p-6 flex items-center justify-center">
                  <img src={product.images[activeImage]} alt={product.name} className="max-w-full max-h-full w-full h-full object-contain" />
                </div>
              </div>

              <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
                <h2 className="text-2xl font-medium text-gray-900 mb-4">Visão geral</h2>
                <div className="space-y-4 text-gray-600 leading-relaxed whitespace-pre-line">
                  {product.overviewText}
                </div>
              </div>
            </div>

            <div>
              <div className="rounded-3xl border border-gray-100 bg-white p-6 md:p-8 shadow-sm">
                {product.badge && (
                  <span className="inline-block bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-4">{product.badge}</span>
                )}

                <h1 className="text-xl md:text-[1.8rem] font-normal text-gray-900 leading-[1.15] tracking-tight mb-5">
                  {product.name}
                </h1>

                {(product.fulfillment || product.officialStore || product.freeShipping) && (
                  <div className="flex items-center gap-2 flex-wrap mb-5">
                    {product.fulfillment && (
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${fulfillmentColors}`}>
                        <i className={fulfillmentIcon}></i>
                        {fulfillmentLabel}
                      </span>
                    )}
                    {product.officialStore && product.officialStoreName && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-blue-700">
                        <i className="ri-verified-badge-fill"></i>
                        {product.officialStoreName}
                      </span>
                    )}
                    {product.freeShipping && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-green-700">
                        <i className="ri-truck-line"></i>
                        Frete grátis
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2.5 mb-6 flex-wrap">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className={`ri-star-fill text-sm ${star <= Math.floor(product.rating) ? "text-amber-400" : "text-gray-200"}`}></i>
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-600">{product.rating.toFixed(1)}</span>
                  <span className="text-sm text-gray-400">({product.reviews.toLocaleString("pt-BR")} avaliações)</span>
                </div>

                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-7">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      {product.originalPrice > product.price && (
                        <p className="text-base text-gray-400 line-through">
                          R$ {product.originalPrice.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        </p>
                      )}
                      {product.officialStoreName && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                          vendido por
                          <span className="text-gray-700">{product.officialStoreName}</span>
                          <i className="ri-verified-badge-fill text-blue-500 text-sm"></i>
                        </span>
                      )}
                    </div>
                    <div className="flex items-end gap-3 flex-wrap">
                      <p className="text-2xl md:text-[2.4rem] font-medium text-gray-900 leading-none">
                        R$ {product.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </p>
                      {product.discount > 0 && (
                        <span className="bg-red-500 text-white text-base font-semibold px-4 py-1.5 rounded-full md:mb-1">
                          -{product.discount}%
                        </span>
                      )}
                    </div>
                  </div>

                  <a
                    href={product.affiliateLink}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full md:w-auto px-7 py-3.5 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg transition-colors whitespace-nowrap"
                  >
                    Ver Oferta
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
                  <h2 className="text-xl font-medium text-gray-900 mb-4">Perguntas frequentes</h2>
                  <div className="space-y-3">
                    {product.faqs.map((faq, index) => {
                      const isOpen = openFaq === index;
                      return (
                        <div key={faq.question} className="rounded-xl border border-gray-100 overflow-hidden bg-white">
                          <button
                            onClick={() => setOpenFaq(isOpen ? null : index)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          >
                            <span className="text-sm font-semibold text-gray-900">{faq.question}</span>
                            <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}></i>
                          </button>
                          {isOpen && (
                            <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">
                              {faq.answer}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5">
                <h2 className="font-bold text-emerald-800 mb-3 text-lg">Pontos fortes</h2>
                <ul className="space-y-2">
                  {product.pros.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-emerald-700">
                      <i className="ri-checkbox-circle-line mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                <h2 className="font-semibold text-gray-700 mb-3 text-lg">Pontos de atenção</h2>
                <ul className="space-y-2">
                  {product.cons.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                      <i className="ri-information-line mt-0.5 text-gray-400"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mb-16">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-medium text-gray-900">Guias e reviews sobre {product.subcategory}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Compare análises editoriais para decidir melhor antes de comprar.
                  </p>
                </div>
                <Link to="/guias" className="text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors whitespace-nowrap">
                  Ver mais artigos
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/artigo/${article.slug}`}
                    className="group bg-white rounded-2xl border border-gray-100 hover:border-orange-200 overflow-hidden transition-all hover:-translate-y-0.5"
                  >
                    <div className="w-full h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-orange-500 text-xs font-bold">{article.category}</span>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {article.type}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-2 group-hover:text-orange-500 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-gray-500 line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-400 gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                        <span>{formatCanonicalDateForDisplay(article.date)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-medium text-gray-900">Produtos relacionados</h2>
              <Link to="/categorias" className="text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors">
                Ver catálogo
                <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>

            {related.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 py-12 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white border border-gray-200 mx-auto mb-4">
                  <i className="ri-links-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-gray-500 font-semibold">Ainda não há produtos relacionados cadastrados</p>
                <p className="text-gray-400 text-sm mt-1">Cadastre mais itens da mesma categoria para preencher esta seção automaticamente.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 justify-items-start">
                {related.map((relatedProduct) => (
                  <OfferCard
                    key={relatedProduct.slug}
                    className="w-full"
                    item={{
                      slug: relatedProduct.slug,
                      image: relatedProduct.image,
                      name: relatedProduct.name,
                      price: relatedProduct.price,
                      originalPrice: relatedProduct.originalPrice,
                      discount: relatedProduct.discount,
                      marketplace: relatedProduct.marketplace,
                      freeShipping: relatedProduct.freeShipping,
                      fulfillment: relatedProduct.fulfillment,
                      officialStoreName: relatedProduct.officialStoreName || (relatedProduct.officialStore ? "Loja Oficial" : ""),
                      affiliateLink: relatedProduct.affiliateLink,
                      isFlash: relatedProduct.badge === "Oferta Relâmpago",
                      category: relatedProduct.category,
                      categorySlug: relatedProduct.categorySlug,
                      subcategory: relatedProduct.subcategory,
                      subcategorySlug: relatedProduct.subcategorySlug,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
